import { useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { List } from 'foundations-components/transitional/components/List';
import { Table } from 'foundations-components/transitional/components/Table';
import { Link } from 'foundations-components/transitional/components/Link';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { createLanguageVariant } from '../../actions/manage/ArticleActions';
import { push } from '../../actions/HistoryActions';
import { showErrorNotification } from '../../actions/Notifications';
import StatusTag from '../../components/StatusTag';
import { SCREENS } from '../../constants/TrackingConstants';
import { getMasterArticle } from '../../utils/multiLanguageUtils';
import { getSettingUrl } from '../../routing/utils/externalUrls';
import UsageTracker from '../../utils/UsageTracker';
import LanguageSwitcher from '../LanguageSwitcher';
import EditArticleLanguageModal from './EditArticleLanguageModal';
import ConfirmDetachFromLanguageGroupModal from '../modals/ConfirmDetachFromLanguageGroupModal';
import AddToGroupModal from './categorize/language/AddToGroupModal';
import { getLanguageNameFromCode } from 'knowledge-content-ui/utils/langUtils';
import { getEditArticlePath } from '../../routing/utils/internalPaths';
import { selectAllLanguages } from '../../knowledge-base/selectors/knowledgeBaseDetails';
import { useAppDispatch, useAppSelector } from '../../utils/hooks';
import CreateLanguageVariantModal from '../modals/CreateLanguageVariantModal';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const STATE_MASTER = 'MASTER';
const STATE_VARIANT = 'VARIANT';
const STATE_UNGROUPED = 'UNGROUPED';
export default function ArticleLanguageVariantSwitcher(props) {
  const {
    article,
    subscreen,
    knowledgeScopes,
    onAddToGroup,
    onRemoveFromGroup
  } = props;
  const {
    values: {
      hs_language: language,
      hs_id: id,
      hs_state: state,
      hs_translated_from_id
    },
    translations
  } = article;
  const dispatch = useAppDispatch();
  const allLanguages = useAppSelector(selectAllLanguages);
  const kbLanguages = [allLanguages.primaryLanguage, ...(allLanguages.additionalLanguages || [])];
  const hasAdditionalLanguages = !!allLanguages.additionalLanguages && allLanguages.additionalLanguages.length > 0;
  const [showCreateVariationModal, setShowCreateVariationModal] = useState(false);
  const [showEditLanguageModal, setShowEditLanguageModal] = useState(false);
  const [showConfirmDisconnectFromGroupModal, setShowConfirmDisconnectFromGroupModal] = useState(false);
  const [showAddToGroupModal, setShowAddToGroupModal] = useState(false);
  const isMaster = !hs_translated_from_id && Object.keys(article.translations).length > 0;
  const isVariant = !!hs_translated_from_id;
  const multiLangGroupState = isMaster ? STATE_MASTER : isVariant ? STATE_VARIANT : STATE_UNGROUPED;
  let articleGroupActionLink;
  switch (multiLangGroupState) {
    case STATE_VARIANT:
      articleGroupActionLink = /*#__PURE__*/_jsx(Tooltip, {
        disabled: hasAdditionalLanguages,
        placement: "right",
        title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "knowledgeContentUI.edit.languageSwitcher.linkToSettingsTooltip_jsx",
          elements: {
            Link: ({
              children
            }) => /*#__PURE__*/_jsx(Link, {
              href: getSettingUrl(''),
              children: children
            })
          }
        }),
        children: /*#__PURE__*/_jsx(Link, {
          paddingInlineStart: "400",
          disabled: !hasAdditionalLanguages,
          onClick: () => setShowConfirmDisconnectFromGroupModal(true),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "knowledgeContentUI.edit.languageSwitcher.languageActions.disconnectFromPrimary"
          })
        })
      });
      break;
    case STATE_UNGROUPED:
      articleGroupActionLink = /*#__PURE__*/_jsx(Tooltip, {
        disabled: hasAdditionalLanguages,
        placement: "right",
        title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "knowledgeContentUI.edit.languageSwitcher.linkToSettingsTooltip_jsx",
          elements: {
            Link: ({
              children
            }) => /*#__PURE__*/_jsx(Link, {
              href: getSettingUrl(''),
              children: children
            })
          }
        }),
        children: /*#__PURE__*/_jsx(Link, {
          paddingInlineStart: "400",
          disabled: !hasAdditionalLanguages,
          onClick: () => setShowAddToGroupModal(true),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "knowledgeContentUI.edit.languageSwitcher.languageActions.connectToPrimary"
          })
        })
      });
      break;
    default:
      articleGroupActionLink = null;
  }
  const onConfirmDisconnectFromGroup = () => {
    setShowConfirmDisconnectFromGroupModal(false);
    onRemoveFromGroup();
  };
  const onAddToGroupConfirm = updatedArticle => {
    setShowAddToGroupModal(false);
    onAddToGroup(updatedArticle);
  };
  const {
    values: {
      hs_language: masterLang,
      hs_name: masterArticleName,
      hs_state: masterArticleState,
      hs_id: masterArticleId
    }
  } = getMasterArticle(article);
  const getHandleClick = articleId => () => dispatch(push(getEditArticlePath(articleId)));
  const handleCreateLanguageVariant = (...args) => {
    UsageTracker.track('createArticleInteraction', {
      screen: SCREENS.CREATE,
      subscreen,
      action: 'create-multi-lang-var-save'
    });
    return dispatch(createLanguageVariant(...args));
  };
  const handleError = (...args) => dispatch(showErrorNotification(...args));
  const handleSuccess = ({
    id: articleId
  }) => dispatch(push(getEditArticlePath(articleId)));
  const handleCreateLanguageVariantCancel = () => {
    UsageTracker.track('createArticleInteraction', {
      screen: SCREENS.CREATE,
      subscreen,
      action: 'create-multi-lang-var-cancel'
    });
    setShowCreateVariationModal(false);
  };
  const handleShowCreateLanguageVariant = () => {
    UsageTracker.track('createArticleInteraction', {
      screen: SCREENS.CREATE,
      subscreen,
      action: 'create-multi-lang-var-click'
    });
    setShowCreateVariationModal(true);
  };
  let languages = {
    [masterLang]: {
      id: masterArticleId,
      isMaster: true,
      language: masterLang,
      state: masterArticleState
    }
  };
  if (hs_translated_from_id) {
    languages[language] = {
      id,
      language,
      state
    };
  }
  languages = Object.keys(translations)
  // the master language is already added on top
  .filter(key => key !== masterLang).reduce((acc, key) => Object.assign({}, acc, {
    [key]: {
      id: translations[key].values.hs_id,
      language: key,
      state: translations[key].values.hs_state
    }
  }), languages);
  const languageVariationsList = /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "heading-50",
      paddingInline: "400",
      paddingBlockStart: "100",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "knowledgeContentUI.edit.languageSwitcher.variations"
      })
    }), /*#__PURE__*/_jsx(Table, {
      bordered: false,
      flush: true,
      hover: true,
      condensed: true,
      children: /*#__PURE__*/_jsx("tbody", {
        children: Object.keys(languages).map(key => /*#__PURE__*/_jsxs("tr", {
          onClick: getHandleClick(languages[key].id),
          children: [/*#__PURE__*/_jsxs("td", {
            style: {
              borderBlockStart: 'none'
            },
            children: [getLanguageNameFromCode(languages[key].language), languages[key].isMaster && /*#__PURE__*/_jsxs(_Fragment, {
              children: ["\xA0", /*#__PURE__*/_jsx(FormattedMessage, {
                message: "knowledgeContentUI.edit.languageSwitcher.primaryLanguage"
              })]
            })]
          }), /*#__PURE__*/_jsx("td", {
            style: {
              borderBlockStart: 'none'
            },
            children: /*#__PURE__*/_jsx(StatusTag, {
              state: languages[key].state,
              accessRuleIds: article.values.hs_access_rule_ids,
              accessRuleType: article.values.hs_access_rule_type
            })
          })]
        }, key))
      })
    })]
  });
  const actionsList = /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "heading-50",
      paddingInline: "400",
      paddingBlockStart: "100",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "knowledgeContentUI.edit.languageSwitcher.languageActions.header"
      })
    }), /*#__PURE__*/_jsxs(List, {
      styled: false,
      wrapChildren: true,
      children: [/*#__PURE__*/_jsx(Link, {
        paddingInlineStart: "400",
        onClick: () => setShowEditLanguageModal(true),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "knowledgeContentUI.edit.languageSwitcher.languageActions.editLanguage"
        })
      }), articleGroupActionLink, /*#__PURE__*/_jsx(Link, {
        paddingInlineStart: "400",
        onClick: handleShowCreateLanguageVariant,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "knowledgeContentUI.edit.languageSwitcher.languageActions.createLanguageVariant"
        })
      })]
    })]
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [showEditLanguageModal && /*#__PURE__*/_jsx(EditArticleLanguageModal, {
      onClose: () => setShowEditLanguageModal(false)
    }), showConfirmDisconnectFromGroupModal && /*#__PURE__*/_jsx(ConfirmDetachFromLanguageGroupModal, {
      articleId: article.values.hs_id,
      articleName: article.values.hs_name,
      onConfirm: onConfirmDisconnectFromGroup,
      onReject: () => setShowConfirmDisconnectFromGroupModal(false)
    }), showAddToGroupModal && /*#__PURE__*/_jsx(AddToGroupModal, {
      article: article,
      kbLanguages: kbLanguages,
      onAddToGroup: onAddToGroupConfirm,
      onClose: () => setShowAddToGroupModal(false)
    }), showCreateVariationModal && /*#__PURE__*/_jsx(CreateLanguageVariantModal, {
      disabledLanguages: Object.keys(languages),
      masterArticleId: masterArticleId,
      masterArticleName: masterArticleName,
      masterLang: masterLang,
      onCancel: handleCreateLanguageVariantCancel,
      onClose: handleCreateLanguageVariantCancel,
      onCreateLanguageVariant: handleCreateLanguageVariant,
      onError: handleError,
      onSuccess: handleSuccess
    }), /*#__PURE__*/_jsxs(LanguageSwitcher, {
      language: language,
      children: [knowledgeScopes.canWriteArticle && /*#__PURE__*/_jsxs(_Fragment, {
        children: [actionsList, /*#__PURE__*/_jsx(View, {
          marginBlockStart: "200",
          marginBlockEnd: "400",
          inlineSize: "100%",
          borderColor: "border-core-default",
          borderWidth: "100",
          borderBlockStartWidth: "0",
          borderStyle: "solid"
        })]
      }), languageVariationsList]
    })]
  });
}